import { useEffect, useState, useCallback } from 'react';

/**
 * Phase 4.4 — branch settings hook.
 *
 * Single source for every toggle / threshold / cap the back-office
 * controls. Cashier+ can read; only ADMIN+ can write via PATCH at
 * /api/settings.
 *
 * Replaces the static featureFlags.ts + client/discountCaps.ts
 * defaults with live values. Defaults below are used until the
 * first fetch resolves so the UI doesn't flicker.
 */

export interface Settings {
  id: string;
  branchId: string;
  tipsEnabled: boolean;
  splitBillEnabled: boolean;
  varianceThresholdCents: number;
  defaultAccountCreditLimitCents: number;
  discountCapBpsCashier: number;
  discountCapBpsSupervisor: number;
  legalName: string | null;
  tradingName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  postalCode: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  vatNumber: string | null;
  invoiceNumberPrefix: string | null;
}

const FALLBACK: Settings = {
  id: '',
  branchId: '',
  tipsEnabled: false,
  splitBillEnabled: true,
  varianceThresholdCents: 20000,
  defaultAccountCreditLimitCents: 50000,
  discountCapBpsCashier: 1000,
  discountCapBpsSupervisor: 2000,
  legalName: null, tradingName: null,
  addressLine1: null, addressLine2: null,
  city: null, postalCode: null,
  country: 'South Africa',
  phone: null, email: null,
  vatNumber: null, invoiceNumberPrefix: null,
};

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/settings', { credentials: 'include' });
      if (!r.ok) {
        if (r.status === 401) return;
        throw new Error(`HTTP ${r.status}`);
      }
      const data = await r.json();
      setSettings(data.settings ?? FALLBACK);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  return { settings, loading, error, refresh };
}
